import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';

export default class GizmoControl extends THREE.Group {
  // #region MAIN
  ctrl: TransformControls; // the control's logic
  helper: THREE.Object3D; // visualization of control
  attachObj?: THREE.Object3D; // Object to attach to allow using the gizmo without a mesh

  onRotate?: (v: Array<number>) => void;
  onMove?: (v: Array<number>) => void;
  onScale?: (v: Array<number>) => void;

  onDragStart?: () => void;
  onDragEnd?: () => void;

  constructor(
    camera: THREE.Camera | THREE.PerspectiveCamera | THREE.OrthographicCamera,
    canvas: HTMLCanvasElement,
  ) {
    super();

    this.ctrl = new TransformControls(camera, canvas);
    this.helper = (this.ctrl as any).getHelper();

    this.ctrl.setSize(0.7);
    this.ctrl.setSpace('local');
    this.add(this.helper);

    this.ctrl.addEventListener('dragging-changed', this.onDragChange);
    this.ctrl.addEventListener('change', this.onChange);
  }

  dispose() {
    this.ctrl.removeEventListener('dragging-changed', this.onDragChange);
    this.ctrl.removeEventListener('change', this.onChange);
  }
  // #endregion

  // #region SETTERS / GETTERS
  get isDragging(): boolean {
    return this.ctrl.dragging;
  }

  setPosition(v: Array<number>): this {
    this.ctrl.object?.position.fromArray(v);
    return this;
  }

  getPosition(): Array<number> {
    return this.ctrl.object ? this.ctrl.object.position.toArray() : [0, 0, 0];
  }

  setQuaternion(v: Array<number>): this {
    this.ctrl.object?.quaternion.fromArray(v);
    return this;
  }

  getQuaternion(): Array<number> {
    return this.ctrl.object
      ? this.ctrl.object.quaternion.toArray()
      : [0, 0, 0, 1];
  }

  setMode(
    s: 'translate' | 'rotate' | 'scale' | 'ztwist' | 'xtwist' | 'ytwist',
  ): this {
    switch (s) {
      case 'xtwist':
        this.ctrl.setMode('rotate');
        this.ctrl.showX = true;
        this.ctrl.showY = false;
        this.ctrl.showZ = false;
        break;
      case 'ytwist':
        this.ctrl.setMode('rotate');
        this.ctrl.showX = false;
        this.ctrl.showY = true;
        this.ctrl.showZ = false;
        break;
      case 'ztwist':
        this.ctrl.setMode('rotate');
        this.ctrl.showX = false;
        this.ctrl.showY = false;
        this.ctrl.showZ = true;
        break;
      default:
        this.ctrl.setMode(s);
        this.ctrl.showX = true;
        this.ctrl.showY = true;
        this.ctrl.showZ = true;
        break;
    }

    return this;
  }

  resetTransform(
    pos?: Array<number>,
    quat?: Array<number>,
    scl?: Array<number>,
  ): this {
    this.ctrl.object?.position.fromArray(pos ?? [0, 0, 0]);
    this.ctrl.object?.quaternion.fromArray(quat ?? [0, 0, 0, 1]);
    this.ctrl.object?.scale.fromArray(scl ?? [1, 1, 1]);
    return this;
  }

  setSpace(s: 'world' | 'local'): this {
    this.ctrl.setSpace(s);
    return this;
  }

  setSize(v: number): this {
    this.ctrl.setSize(v);
    return this;
  }

  getCurrentTransform(): {
    pos: Array<number>;
    rot: Array<number>;
    scl: Array<number>;
  } | null {
    if (this.ctrl.object) {
      return {
        pos: this.ctrl.object.position.toArray(),
        scl: this.ctrl.object.scale.toArray(),
        rot: this.ctrl.object.quaternion.toArray(),
      };
    }
    return null;
  }

  getEuler(): Array<number> | null {
    return this.ctrl.object
      ? (this.ctrl.object.rotation.toArray() as Array<number>)
      : null;
  }
  // #endregion

  // #region METHODS
  show(): this {
    this.ctrl.enabled = true;
    this.helper.visible = true;
    return this;
  }

  hide(): this {
    this.ctrl.enabled = false;
    this.helper.visible = false;
    return this;
  }

  detachless(): this {
    if (!this.attachObj) {
      // NOTE: Newer version of threejs requires the attached
      // object be part of the scene else it will constantly error.
      // To avoid this, the object3D will need to be part of the scene.
      this.attachObj = new THREE.Object3D();
      this.add(this.attachObj);
    }
    this.ctrl.attach(this.attachObj);
    return this;
  }
  // #endregion

  // #region HANDLERS
  onDragChange = (e: any) => {
    if (e.value && this.onDragStart) this.onDragStart();
    else if (!e.value && this.onDragEnd) this.onDragEnd();
  };

  onChange = () => {
    const o = this.ctrl.object;
    if (o && this.ctrl.dragging) {
      switch (this.ctrl.mode) {
        case 'translate':
          if (this.onMove) this.onMove(o.position.toArray());
          break;
        case 'rotate':
          if (this.onRotate) this.onRotate(o.quaternion.toArray());
          break;
        case 'scale':
          if (this.onScale) this.onScale(o.scale.toArray());
          break;
      }
    }
  };
  // #endregion
}
